import { Link, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import type { DashboardBucket } from '@/api/client';
import type { RmmBucket, RmmTone, RmmTrendPoint } from '@/api/rmm';
import { CategoryBar, TrendLines } from '@/components/dashboard/charts';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

/**
 * Shared pieces for the RMM dashboards. Colour rules (dataviz skill):
 *  - status-like data (priority, patch/AV status, readiness) uses the theme's status tokens,
 *    always next to a label — never colour alone;
 *  - magnitude-by-category (types, OS, clients) is ONE hue — position + axis label carry identity;
 *  - multi-series trends use the validated --chart-series-1/2 pair, with a legend.
 */

const TONE_COLOR: Record<RmmTone, string> = {
  // "serious" sits between warning and critical (e.g. High vs Critical alerts): the destructive
  // hue, lightened — tone-on-tone, and always beside its label.
  serious: 'hsl(var(--destructive) / 0.55)',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  destructive: 'hsl(var(--destructive))',
  info: 'hsl(var(--info))',
  neutral: 'hsl(var(--neutral))',
  /** A second, quieter neutral (e.g. "No data" beside "No policy"). */
  subtle: 'hsl(var(--neutral) / 0.45)',
};

/** Status buckets → coloured by their tone. */
export const toned = (b: RmmBucket[]): DashboardBucket[] =>
  b.map((x) => ({ id: x.id, label: x.label, value: x.value, color: TONE_COLOR[x.tone ?? 'neutral'] }));

/** Category buckets → one hue. */
export const oneHue = (b: RmmBucket[]): DashboardBucket[] =>
  b.map((x) => ({ id: x.id, label: x.label, value: x.value, color: 'var(--chart-series-1)' }));

export const SERIES = ['var(--chart-series-1)', 'var(--chart-series-2)'] as const;

/** Horizontal bar chart sized to its rows, with room for long labels (client names). */
export function HBar({ data, onSelect, name = 'Devices' }: { data: DashboardBucket[]; onSelect?: (b: DashboardBucket) => void; name?: string }) {
  return (
    <CategoryBar data={data} orientation="bars" valueFormatter={count} name={name} onSelect={onSelect}
      height={Math.max(200, data.length * 30 + 40)} labelWidth={170} labelMaxChars={24} />
  );
}

// -------- Client scope --------

/**
 * The dashboards follow the header's client scope (ClientScopeContext): a picked client → that
 * company's figures (`?companyId=` on the dashboard API), otherwise the whole fleet. The old
 * per-dashboard dropdown and its `cosmos:rmm:dashboard:client` preference are gone.
 */
export function useRmmDashboardClient(): number | null {
  return useScopeFilter().companyId;
}

// -------- Overview / Patching tabs --------

const TABS = [
  { to: ROUTES.rmm.dashboards.overview, label: 'Overview' },
  { to: ROUTES.rmm.dashboards.patching, label: 'Patching' },
];

export function RmmDashboardTabs() {
  const { pathname } = useLocation();
  return (
    <div className="flex rounded-md border border-border bg-card p-0.5">
      {TABS.map((t) => (
        <Link key={t.to} to={t.to}
          className={cn('rounded px-3 py-1 text-sm font-medium transition-colors',
            pathname === t.to ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground')}>
          {t.label}
        </Link>
      ))}
    </div>
  );
}

// -------- Trends --------

const fmtDay = (d: string) => { try { return format(new Date(`${d}T00:00:00`), 'd MMM'); } catch { return d; } };

/** Legend row for multi-series charts (identity is never colour alone). */
export function SeriesLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-3 rounded" style={{ background: i.color }} />{i.label}
        </span>
      ))}
    </div>
  );
}

/**
 * A trend over the daily snapshots. History only starts when Cosmos began recording,
 * so with < 2 days it explains that instead of drawing a single dot.
 */
export function SnapshotTrend<T extends { day: string } = RmmTrendPoint>({
  trend, series, valueFormatter, height = 220,
}: {
  trend: T[];
  series: { key: keyof T & string; label: string; color: string }[];
  valueFormatter?: (n: number) => string;
  height?: number;
}) {
  if (trend.length < 2) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 text-center text-xs text-muted-foreground" style={{ height }}>
        <span className="font-medium text-foreground">Collecting history</span>
        <span>
          Cosmos records a daily snapshot{trend[0] ? ` (first: ${fmtDay(trend[0].day)})` : ''}. The trend appears once there are two days.
        </span>
      </div>
    );
  }
  return (
    <TrendLines
      data={trend}
      xKey="day"
      xTickFormatter={fmtDay}
      valueFormatter={valueFormatter}
      height={height}
      series={series.map((s) => ({ key: s.key, label: s.label, color: s.color }))}
    />
  );
}

export const pct = (n: number) => `${Math.round(n)}%`;
export const count = (n: number) => String(Math.round(n));
